import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AntibodyDomainSearchInput} from 'antibodies/actions/AntibodyDomainSearchInput';
import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import {AntibodyFormat, DistinctDomain} from 'antibodies/AntibodyTypedefs';
import {distinctDomainToKey, formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import Antibody from 'antibodies/visualization/Antibody';
import {FONT_PROXIMA_12} from 'styles/FontConstants';

export type DomainWithProtein = DistinctDomain & {proteinId: string | null};

export interface AntibodyDomainInputsProps {
  disabled: boolean;
  format: AntibodyFormat;
  domainsWithProteinByKey: Record<string, DomainWithProtein>;
  setDomainsWithProteinByKey: (newDomainsWithProteinByKey: Record<string, DomainWithProtein>) => void;
}

/**
 * A set of ChipSearchInput that can be used to associate AA Sequence entities with antibody domains.
 * @param disabled Whether the ChipSearchInput should be disabled
 * @param format The format of the antibody being created
 * @param domainsWithProteinByKey A map domain keys to domains with their associated protein
 * @param setDomainsWithProteinByKey A function to update domainsWithProteinByKey
 */
export default function AntibodyDomainInputs({
  disabled,
  format,
  domainsWithProteinByKey,
  setDomainsWithProteinByKey,
}: AntibodyDomainInputsProps): JSX.Element {
  const antibodyRestProps = ANTIBODY_REST_PROPS_BY_ID[format.id];
  const sortedDistinctDomains = formatToSortedDistinctDomains(format);

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.column)}>
        <div className={css(styles.columnHeader)}>Preview</div>
        <div className={css(styles.vizWrapper)}>
          <Antibody format={format} {...antibodyRestProps} />
        </div>
      </div>
      <div className={css(styles.column)}>
        <div className={css(styles.columnHeader)}>Domains</div>
        <div>
          {sortedDistinctDomains.map((distinctDomain) => {
            const distinctDomainKey = distinctDomainToKey(distinctDomain);
            return (
              <AntibodyDomainSearchInput
                key={distinctDomainKey}
                disabled={disabled}
                distinctDomain={distinctDomain}
                selectedProtein={domainsWithProteinByKey[distinctDomainKey]?.proteinId}
                setSelectedProtein={(newSelectedProtein) => {
                  setDomainsWithProteinByKey({
                    ...domainsWithProteinByKey,
                    [distinctDomainKey]: {
                      ...distinctDomain,
                      proteinId: newSelectedProtein,
                    },
                  });
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  columnHeader: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_700,
    fontWeight: 600,
    marginBottom: '8px',
  },
  vizWrapper: {
    backgroundColor: Color.GRAY_50,
    borderRadius: '8px',
    padding: '32px',
  },
  wrapper: {
    display: 'flex',
    gap: '16px',
  },
});
